import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  OnChangeFn,
  Row,
  SortingState,
  useReactTable
} from '@tanstack/react-table';

import { Restricted, TableHeader } from '@/common';
import { PaginationArrow } from '@/common/PaginationArrow';
import { IconButton, PARAGRAPH2_NORMAL_CLASSES } from '@/components/generic';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { AuthContext } from '@/contexts';
import {
  ArrowDownBlackIcon as ArrowDownIcon,
  ArrowUpIcon,
  EditIcon,
  EditPenSmallIcon,
  EllipsisIcon,
  ThrashIconSmall,
  TickCircle
} from '@/resources';
import { getTestId, TestIds } from '@/shared/test-ids';
import { AllowedPermissions, TableData } from '@/types';

type TableProps = {
  data: TableData[];
  columns: ColumnDef<TableData>[];
  tableTitle?: string;
  permissions: AllowedPermissions;
  addButtonLabel?: string;
  sorting?: SortingState;
  setSorting?: OnChangeFn<SortingState>;
  search?: (query: string) => void;
  handleCreate?: () => void;
  handleEdit?: (id: TableData) => void;
  handleDelete?: (id: TableData) => void;
  handleViewDetails?: (id: TableData) => void;
  handleOpenFilterModal?: () => void;
  fetchNextPage: () => void;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  widerLayout?: boolean;
  numberOfAppliedFilters: number;
  hiddenColumnsMobile: string[];
  mobileColumnOffset?: number;
  isDeviceTable?: boolean;
  containerClassName?: string;
  columnWidth?: string;
  isDashboardPage?: boolean;
  filterButtonRef?: React.Ref<HTMLDivElement>;
  selectedRowIds?: string[];
  onSelect?: (row: TableData) => void;
  isRowDisabled?: (row: TableData) => boolean;
  testIdPrefix?: string;
  downloadButtonLabel?: string;
  downloadUrl?: string;
};

export const Table = ({
  data,
  columns,
  tableTitle,
  addButtonLabel,
  permissions,
  sorting,
  setSorting,
  search,
  handleCreate,
  handleEdit,
  handleDelete,
  handleViewDetails,
  handleOpenFilterModal,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  widerLayout,
  numberOfAppliedFilters,
  hiddenColumnsMobile,
  mobileColumnOffset = 3,
  containerClassName = 'max-h-[80vh] 3xl:max-h-[84vh] md:mx-[35px] 4xl:max-h-[84vh]',
  columnWidth = 'w-[43vw]',
  isDashboardPage,
  filterButtonRef,
  selectedRowIds,
  onSelect,
  isRowDisabled,
  testIdPrefix,
  downloadButtonLabel,
  downloadUrl
}: TableProps) => {
  const { currentUserData: currentUser } = useContext(AuthContext);
  const { ref, inView } = useInView();
  const [showMobileOptions, setShowMobileOptions] = useState<boolean>(false);
  const [rowId, setRowId] = useState<string>();
  const [t] = useTranslation('common');
  const prefix = testIdPrefix || TestIds.common.tablePrefix;

  const table = useReactTable({
    data: data,
    columns,
    initialState: {
      columnVisibility: {
        organization: !currentUser?.organizations?.length || currentUser?.organizations?.length > 1,
        organizations:
          !currentUser?.organizations?.length || currentUser?.organizations?.length > 1,
        organizationName:
          !currentUser?.organizations?.length || currentUser?.organizations?.length > 1
      }
    },
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    state: { sorting },
    manualFiltering: true,
    manualSorting: true,
    enableSortingRemoval: false,
    debugTable: true
  });

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView]);

  const getIcon = (column: Column<TableData>) => {
    switch (column.getIsSorted()) {
      case 'asc':
        return <ArrowUpIcon className='ml-2 size-2' />;
      case 'desc':
        return <ArrowDownIcon className='ml-2 size-2' />;
      default:
        return (
          <div className='ml-2 flex flex-col'>
            <ArrowUpIcon className='size-2' />
            <ArrowDownIcon className='size-2' />
          </div>
        );
    }
  };

  const openOptions = (row: Row<TableData>) => {
    setRowId(row.id);
    setShowMobileOptions(!showMobileOptions);
  };

  return (
    <div className='flex w-full flex-col justify-between'>
      {(tableTitle || addButtonLabel || handleCreate) && (
        <TableHeader
          tableTitle={tableTitle as string}
          addButtonLabel={addButtonLabel as string}
          createPermission={permissions.createPermission}
          search={search}
          handleCreate={handleCreate as () => void}
          handleOpenFilterModal={handleOpenFilterModal && handleOpenFilterModal}
          numberOfAppliedFilters={numberOfAppliedFilters}
          filterButtonRef={filterButtonRef}
          testIdPrefix={prefix}
          downloadButtonLabel={downloadButtonLabel}
          downloadUrl={downloadUrl}
        />
      )}
      <div
        data-testid={getTestId(prefix, 'table')}
        className={`no-scrollbar z-10 overflow-auto rounded-lg pb-2.5 ${containerClassName}`}
        style={isDashboardPage ? { maxHeight: 'calc(100vh - 430px)' } : {}}>
        <table className={`${isDashboardPage && 'table-fixed'} w-full flex-1`}>
          <thead className='sticky top-0 z-30 hidden h-12 overflow-hidden bg-primary-gray md:table-header-group'>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={isDashboardPage ? { width: `${header.column.columnDef.size}px` } : {}}
                    className={`${PARAGRAPH2_NORMAL_CLASSES} px-5 py-2.5 text-accent-dark-gray`}
                    onClick={header.column.getToggleSortingHandler()}>
                    <div
                      className={`flex flex-row items-center text-base ${
                        header.column.columnDef.enableSorting && 'cursor-pointer'
                      }`}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.columnDef.enableSorting && getIcon(header.column)}
                    </div>
                  </th>
                ))}
                <th />
              </tr>
            ))}
          </thead>
          <tbody className='z-10 text-center md:border-0'>
            {table.getRowModel().rows.map((row) => {
              const disabled = isRowDisabled?.(row.original);
              return (
                <tr
                  key={row.id}
                  data-testid={getTestId(prefix, `row-${row.original?.id ?? row.id}`)}
                  id={row.id}
                  onClick={
                    !disabled && handleViewDetails
                      ? () => handleViewDetails(row.original)
                      : undefined
                  }
                  className={`${widerLayout ? 'flex flex-row' : 'grid'} ${disabled ? 'pointer-events-none cursor-not-allowed opacity-50' : 'cursor-pointer'} relative grid-flow-col grid-rows-2 border-b border-solid border-secondary-pale-blue-gray py-2.5 md:my-0 md:table-row`}>
                  {row.getVisibleCells().map((cell, index) => {
                    return (
                      // The fragment is the mapped child, so the key belongs here rather than only
                      // on the cells inside it.
                      <Fragment key={cell.id}>
                        {!hiddenColumnsMobile?.includes(cell.column.id) && (
                          <td
                            key={cell.id + index}
                            className={`${
                              widerLayout &&
                              !row.original.enabled &&
                              'font-normal text-secondary-blue-gray'
                            } ${columnWidth} truncate text-left text-sm text-primary-black md:hidden`}
                            style={
                              isDashboardPage ? { width: `${cell.column.columnDef.size}px` } : {}
                            }>
                            {(() => {
                              if (cell.column.id === 'name') {
                                return (
                                  <div className='flex w-full flex-row gap-4 font-bold'>
                                    {/* A div, not a p: name cells render block content of their
                                        own (status tags, icons), which is invalid inside a p. */}
                                    <div className='truncate'>
                                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </div>
                                    {flexRender(
                                      row.getVisibleCells()[index + mobileColumnOffset]?.column
                                        .columnDef.cell,
                                      cell.getContext()
                                    )}
                                  </div>
                                );
                              } else {
                                return flexRender(cell.column.columnDef.cell, cell.getContext());
                              }
                            })()}
                          </td>
                        )}
                        <td
                          key={cell.id}
                          className={`${
                            !row.original?.enabled && 'font-normal text-secondary-blue-gray'
                          } hidden max-w-[600px] truncate break-words px-5 py-2.5 text-left text-primary-black md:table-cell`}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      </Fragment>
                    );
                  })}
                  {(handleDelete || handleEdit) && (
                    <td
                      className='z-20 order-5 row-span-2 ml-4 flex items-center justify-end md:hidden'
                      onClick={(e) => {
                        e.stopPropagation();
                        openOptions(row);
                      }}>
                      <EllipsisIcon className='h-6' />
                    </td>
                  )}
                  {(handleEdit || handleDelete) && (
                    <td className='hidden py-2.5 md:table-cell'>
                      <div className='z-0 flex w-full justify-end pr-5'>
                        {permissions.editPermission && (
                          <Restricted to={[permissions.editPermission]}>
                            <div className='mr-4'>
                              <IconButton
                                dataTestId={getTestId(prefix, 'edit-button')}
                                variant={IconButtonVariant.SECONDARY}
                                handleOnClick={
                                  handleEdit &&
                                  ((e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    handleEdit(row.original);
                                  })
                                }>
                                <EditPenSmallIcon className='h-4 w-[17px] fill-secondary-dark-steel-blue' />
                              </IconButton>
                            </div>
                          </Restricted>
                        )}
                        {permissions.deletePermission && (
                          <Restricted to={[permissions.deletePermission]}>
                            <IconButton
                              dataTestId={getTestId(prefix, 'delete-button')}
                              variant={IconButtonVariant.SECONDARY}
                              handleOnClick={
                                handleDelete &&
                                ((e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  handleDelete(row.original);
                                })
                              }>
                              <ThrashIconSmall className='fill-secondary-dark-steel-blue' />
                            </IconButton>
                          </Restricted>
                        )}
                      </div>
                    </td>
                  )}
                  {row.original?.id && selectedRowIds?.includes(row.original.id.toString()) && (
                    <td>
                      <div
                        onClick={() => onSelect && onSelect(row.original)}
                        className='w-fit rounded-full bg-primary-blue p-1'>
                        <TickCircle className='text-white' />
                      </div>
                    </td>
                  )}
                  {showMobileOptions && row.id === rowId && (
                    <div
                      className={
                        'filter-pop-up-shadow absolute right-5 z-50 mt-2 h-20 w-24 rounded-xl bg-white px-3 md:hidden'
                      }>
                      {permissions.editPermission && handleEdit && (
                        <Restricted to={[permissions.editPermission]}>
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(row.original);
                            }}
                            className='my-3.5 flex flex-row items-center gap-2 text-sm'>
                            <EditIcon className='fill-primary-black' />
                            {t('edit')}
                          </div>
                        </Restricted>
                      )}
                      {permissions.deletePermission && handleDelete && (
                        <Restricted to={[permissions.deletePermission]}>
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(row.original);
                            }}
                            className='mb-3.5 flex flex-row items-center gap-2 text-sm'>
                            <ThrashIconSmall className='fill-primary-black' />
                            {t('delete')}
                          </div>
                        </Restricted>
                      )}
                    </div>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        <div ref={ref}></div>
      </div>
      <PaginationArrow isFetchingNextPage={isFetchingNextPage} inView={inView} />
    </div>
  );
};
